import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Loader2,
    RefreshCw,
    Trash2,
    Mail,
    Search,
    CheckCircle2,
    XCircle,
    ChevronLeft,
    ChevronRight,
    UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AdminShell from "@/components/AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

const LEAD_STATUSES = [
    { value: "new", label: "New", color: "border-ember/40 text-ember" },
    {
        value: "contacted",
        label: "Contacted",
        color: "border-amber-400/40 text-amber-400",
    },
    {
        value: "qualified",
        label: "Qualified",
        color: "border-sky-400/40 text-sky-400",
    },
    {
        value: "won",
        label: "Won",
        color: "border-emerald-400/40 text-emerald-400",
    },
    {
        value: "lost",
        label: "Lost",
        color: "border-white/15 text-bone-500",
    },
];

const statusMeta = (s) =>
    LEAD_STATUSES.find((x) => x.value === s) || LEAD_STATUSES[0];

export default function AdminDashboard() {
    const { user, token, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [contacts, setContacts] = useState([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState(null);
    const [query, setQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), 300);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        setSkip(0);
    }, [debouncedQuery]);

    const refresh = async () => {
        if (!token) return;
        setBusy(true);
        try {
            const params = { skip, limit: PAGE_SIZE };
            if (debouncedQuery) params.q = debouncedQuery;
            const { data } = await axios.get(`${API}/contact`, {
                headers,
                params,
            });
            setContacts(data.items);
            setTotal(data.total);
            if (data.items.length) {
                if (
                    !selected ||
                    !data.items.find((c) => c.id === selected.id)
                ) {
                    setSelected(data.items[0]);
                }
            } else {
                setSelected(null);
            }
        } catch (err) {
            if (err?.response?.status === 401) {
                logout();
                navigate("/admin/login");
                return;
            }
            toast.error("Could not load briefs");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, skip, debouncedQuery]);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this brief? This cannot be undone."))
            return;
        try {
            await axios.delete(`${API}/contact/${id}`, { headers });
            toast.success("Brief deleted");
            if (contacts.length === 1 && skip > 0) {
                setSkip(Math.max(skip - PAGE_SIZE, 0));
            } else {
                refresh();
            }
        } catch {
            toast.error("Delete failed");
        }
    };

    const onChangeStatus = async (id, lead_status) => {
        try {
            const { data } = await axios.patch(
                `${API}/contact/${id}`,
                { lead_status },
                { headers },
            );
            setContacts((cs) => cs.map((c) => (c.id === id ? data : c)));
            if (selected?.id === id) setSelected(data);
        } catch {
            toast.error("Could not update status");
        }
    };

    const onConvert = async () => {
        if (!selected) return;
        if (
            !window.confirm(
                `Create a client account for ${selected.email} and send the welcome email?`,
            )
        )
            return;
        try {
            const { data } = await axios.post(
                `${API}/admin/contact/${selected.id}/convert`,
                {},
                { headers },
            );
            toast.success(`Client created — invite sent to ${data.email}`);
            navigate(`/admin/clients/${data.id}`);
        } catch (err) {
            const msg =
                err?.response?.data?.detail || "Could not convert brief";
            toast.error(typeof msg === "string" ? msg : "Conversion failed");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace />;

    const page = Math.floor(skip / PAGE_SIZE) + 1;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <AdminShell>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <section className="lg:col-span-5 xl:col-span-4 border border-white/10 bg-ink-800/30 flex flex-col max-h-[calc(100vh-7rem)]">
                    <div className="p-4 border-b border-white/10 flex items-center gap-2">
                        <Search className="w-4 h-4 text-bone-500" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search name, email, message…"
                            data-testid="admin-search"
                            className="flex-1 bg-transparent outline-none text-sm placeholder:text-bone-500"
                        />
                        <button
                            onClick={refresh}
                            data-testid="admin-refresh"
                            className="text-bone-500 hover:text-ember"
                        >
                            <RefreshCw
                                className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`}
                            />
                        </button>
                    </div>
                    <div className="px-4 py-2 border-b border-white/10 text-[10px] uppercase tracking-[0.18em] text-bone-500">
                        {total} brief{total === 1 ? "" : "s"}
                    </div>
                    <ul className="flex-1 overflow-y-auto divide-y divide-white/10">
                        {busy && contacts.length === 0 && (
                            <li className="p-6 text-bone-500 text-sm">
                                Loading briefs…
                            </li>
                        )}
                        {!busy && contacts.length === 0 && (
                            <li className="p-6 text-bone-500 text-sm">
                                No briefs match.
                            </li>
                        )}
                        {contacts.map((c) => {
                            const active = selected?.id === c.id;
                            const meta = statusMeta(c.lead_status);
                            return (
                                <li key={c.id}>
                                    <button
                                        onClick={() => setSelected(c)}
                                        data-testid={`brief-item-${c.id}`}
                                        className={`w-full text-left px-5 py-4 transition-colors ${
                                            active
                                                ? "bg-ember/10 border-l-2 border-l-ember"
                                                : "hover:bg-white/5 border-l-2 border-l-transparent"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-3 mb-1">
                                            <span className="font-display text-bone text-base tracking-tight truncate">
                                                {c.name}
                                            </span>
                                            <span
                                                className={`text-[10px] uppercase tracking-[0.18em] border px-1.5 py-0.5 ${meta.color}`}
                                            >
                                                {meta.label}
                                            </span>
                                        </div>
                                        <div className="text-bone-300 text-xs truncate mb-1">
                                            {c.company || c.email}
                                        </div>
                                        <div className="text-bone-500 text-xs line-clamp-2">
                                            {c.message}
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    {pages > 1 && (
                        <div
                            data-testid="admin-pagination"
                            className="border-t border-white/10 p-3 flex items-center justify-between text-xs uppercase tracking-[0.18em]"
                        >
                            <span className="text-bone-500">
                                Page {page} of {pages}
                            </span>
                            <div className="flex gap-1">
                                <button
                                    onClick={() =>
                                        setSkip(Math.max(skip - PAGE_SIZE, 0))
                                    }
                                    disabled={skip === 0}
                                    data-testid="admin-page-prev"
                                    className="inline-flex items-center justify-center w-9 h-9 border border-white/15 hover:border-ember hover:text-ember disabled:opacity-30 transition-colors"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() =>
                                        setSkip(
                                            Math.min(
                                                skip + PAGE_SIZE,
                                                (pages - 1) * PAGE_SIZE,
                                            ),
                                        )
                                    }
                                    disabled={page >= pages}
                                    data-testid="admin-page-next"
                                    className="inline-flex items-center justify-center w-9 h-9 border border-white/15 hover:border-ember hover:text-ember disabled:opacity-30 transition-colors"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                {/* Detail */}
                <section className="lg:col-span-7 xl:col-span-8 border border-white/10 bg-ink-800/30 min-h-[60vh]">
                    {!selected ? (
                        <div className="p-10 text-bone-500 text-sm">
                            Select a brief to view details.
                        </div>
                    ) : (
                        <article className="p-8 lg:p-12">
                            <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-3">
                                        Brief ·{" "}
                                        {new Date(
                                            selected.created_at,
                                        ).toLocaleString()}
                                    </div>
                                    <h2 className="font-display text-bone text-4xl lg:text-5xl tracking-tightest leading-[1.05]">
                                        {selected.name}
                                    </h2>
                                    {selected.company && (
                                        <div className="text-bone-300 mt-2 text-lg">
                                            {selected.company}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {!selected.converted_client_id && (
                                        <button
                                            onClick={onConvert}
                                            data-testid="brief-convert"
                                            className="btn-ember inline-flex items-center gap-2 h-11 px-4 text-xs uppercase tracking-[0.2em]"
                                        >
                                            <UserPlus className="w-4 h-4" />
                                            Convert to client
                                        </button>
                                    )}
                                    {selected.converted_client_id && (
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `/admin/clients/${selected.converted_client_id}`,
                                                )
                                            }
                                            data-testid="brief-open-client"
                                            className="btn-outline-bone inline-flex items-center gap-2 h-11 px-4 text-xs uppercase tracking-[0.2em]"
                                        >
                                            Open client
                                        </button>
                                    )}
                                    <a
                                        href={`mailto:${selected.email}?subject=Re: your brief to Aximo Studios`}
                                        data-testid="brief-reply"
                                        className="btn-outline-bone inline-flex items-center gap-2 h-11 px-4 text-xs uppercase tracking-[0.2em]"
                                    >
                                        <Mail className="w-4 h-4" />
                                        Reply
                                    </a>
                                    <button
                                        onClick={() => onDelete(selected.id)}
                                        data-testid="brief-delete"
                                        className="inline-flex items-center gap-2 h-11 px-4 border border-white/15 hover:border-red-400 hover:text-red-400 transition-colors text-xs uppercase tracking-[0.2em]"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Lead status pills */}
                            <div className="flex items-center gap-2 mb-8 flex-wrap">
                                <span className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mr-2">
                                    Status
                                </span>
                                {LEAD_STATUSES.map((s) => (
                                    <button
                                        key={s.value}
                                        onClick={() =>
                                            onChangeStatus(selected.id, s.value)
                                        }
                                        data-testid={`status-${s.value}`}
                                        className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-1 transition-colors ${
                                            selected.lead_status === s.value
                                                ? s.color
                                                : "border-white/10 text-bone-500 hover:border-white/30"
                                        }`}
                                    >
                                        {s.label}
                                    </button>
                                ))}
                            </div>

                            <dl className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-8">
                                <Field
                                    label="Email"
                                    value={selected.email}
                                    link={`mailto:${selected.email}`}
                                />
                                <Field
                                    label="Service"
                                    value={selected.service || "—"}
                                />
                                <Field
                                    label="Budget"
                                    value={selected.budget || "—"}
                                    highlight={Boolean(selected.budget)}
                                />
                                <Field
                                    label="Email sent"
                                    value={
                                        selected.email_sent ? (
                                            <span className="inline-flex items-center gap-1.5 text-emerald-400">
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                Yes
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-bone-500">
                                                <XCircle className="w-3.5 h-3.5" />
                                                No
                                            </span>
                                        )
                                    }
                                />
                            </dl>

                            <div>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                                    Message
                                </div>
                                <p className="text-bone text-base lg:text-lg leading-relaxed whitespace-pre-wrap">
                                    {selected.message}
                                </p>
                            </div>
                        </article>
                    )}
                </section>
            </div>
        </AdminShell>
    );
}

function Field({ label, value, link, highlight }) {
    const Inner = link ? (
        <a href={link} className="link-underline text-bone hover:text-ember">
            {value}
        </a>
    ) : (
        <span className={highlight ? "text-ember" : "text-bone"}>{value}</span>
    );
    return (
        <div className="bg-ink-800 p-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </div>
            <div className="font-display text-base tracking-tight">
                {Inner}
            </div>
        </div>
    );
}
